import React, { useEffect, useState } from "react";
import TableCompo from "../Table/TableCompo";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  CardContent,
  DialogContent,
  TextField,
  Button,
  useMediaQuery, useTheme
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import {
  fetchDiamondPointOrdersRequest,
  verifyDiamondPointOrderRequest,
} from "../../../redux/Order/RedeemOrdersSlice";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

const DiamondPointsOrder = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const { convertToCsv } = useCsvConverter();
  const redeemOrders = useSelector((state) => state.redeemOrders || {});
  const { diamondPointOrdersData = [], totalDiamondPoint = 0 } = redeemOrders;

  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const formatDateTimeRange = (date, type) => {
  if (!date) return null;
  const d = new Date(date);
  if (type === "start") {
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
  if (type === "end") {
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }
};

  useEffect(() => {
    const payload = { start: 0, limit, search };
    if (startDate && endDate) {
      payload.startDate = formatDateTimeRange(startDate, "start");
      payload.endDate = formatDateTimeRange(endDate, "end");
    }
    dispatch(
      fetchDiamondPointOrdersRequest(payload)
    );
  }, [dispatch, page, limit, search, ]);

  const handleGetDetails = () => {
  if (!startDate || !endDate) {
    toast.error("Please select both start and end dates.");
    return;
  }
  if (startDate > endDate) {
    toast.error("Start Date cannot be after End Date");
    return;
  }

  setPage(0);
  dispatch(
    fetchDiamondPointOrdersRequest({
      start: 0,
      limit,
      search,
      startDate: formatDateTimeRange(startDate, "start"),
      endDate: formatDateTimeRange(endDate, "end"),
    })
  );
};

  const { error, lastAction } = useSelector(
    (state) => state.redeemOrders || {}
  );

  useEffect(() => {
    if (error && lastAction === "createRedeem") {
      toast.error(error);
    }
  }, [error, lastAction]);

  const [selectedRow, setSelectedRow] = useState(null);

  const handleEditClick = (row) => {
    setSelectedId(row._id);
    setSelectedRow(row);
    setOpen(true);
  };

  const handleApprove = () => {
    if (!selectedRow || !inputValue) {
      alert("Missing data");
      return;
    }

    dispatch(
      verifyDiamondPointOrderRequest({
        orderId: selectedRow._id,
        otp: inputValue,
      })
    );

    setOpen(false);
    setInputValue("");
  };

  const handleTableChange = (action, tableState) => {
    const dateParams =
    startDate && endDate
      ? {
          startDate: formatDateTimeRange(startDate, "start"),
          endDate: formatDateTimeRange(endDate, "end"),
        }
      : {};
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        dispatch(
          fetchDiamondPointOrdersRequest({
            start: tableState.page * tableState.rowsPerPage,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          })
        );
        break;

      case "changeRowsPerPage":
        setLimit(tableState.rowsPerPage);
        setPage(0);
        dispatch(
          fetchDiamondPointOrdersRequest({
            start: 0,
            limit: tableState.rowsPerPage,
            search,
            ...dateParams,
          })
        );
        break;

      case "search":
        const searched = tableState.searchText || "";
        setSearch(searched);
        setPage(0);
        dispatch(
          fetchDiamondPointOrdersRequest({
            start: 0,
            limit,
            search: searched,
            ...dateParams,
          })
        );
        break;

      default:
        break;
    }
  };

  const [permissions, setPermissions] = useState(null);
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Invalid permissions JSON", e);
        setPermissions({});
      }
    } else {
      setPermissions({});
    }
  }, []);

  if (permissions === null) {
    return null;
  }
  const canEdit = canShowPermission(permissions, "diamondPointsOrder", "edit");

  const columns = [
    ...(canEdit
      ? [
          {
            field: "_id-edit",
            headerName: "Edit",
            minWidth: 100,
            render: (value, row) =>
              row.status === "PENDING" ? (
                <CiEdit
                  style={{ cursor: "pointer", fontSize: "24px" }}
                  onClick={() => handleEditClick(row)}
                />
              ) : null,
          },
        ]
      : []),
    {
      field: "createdAt",
      headerName: "Created At",
      render: (val, row) => {
        return row?.createdAt
          ? new Date(row.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "N.A";
      },
    },
    {
      field: "customer",
      headerName: "Customer Name",
      render: (val, row) =>
        row.customer
          ? `${row.customer.firstName || ""} ${row.customer.middleName || ""} ${row.customer.lastName || ""}`
          : "N.A",
    },
    {
      field: "phone",
      headerName: "Phone",
      render: (val, row) =>
        row.customer ? `${row.customer.phone || ""}` : "N.A",
    },
    {
      field: "amount",
      headerName: "Amount",
    },
    {
      field: "pointsRedeemed",
      headerName: "Points Redeemed",
    },
    {
      field: "status",
      headerName: "Status",
      render: (val, row) => {
        let color;
        switch (row.status) {
          case "SUCCESS":
            color = "green";
            break;
          case "PENDING":
            color = "blue";
            break;
          default:
            color = "red";
            break;
        }

        return <span style={{ color, fontWeight: "bold" }}>{row.status}</span>;
      },
    },
  ];

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    const query = {};
  if (startDate && endDate) {
    query.startDate = formatDateTimeRange(startDate, "start");
    query.endDate = formatDateTimeRange(endDate, "end");
  }
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/diamond-point/getDiamondPointCsv",
        query,
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "diamond-point.csv");
        },
      })
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2} fontFamily={"Aeonik"}>
          Diamond Point Order List
        </Typography>

        <Box display="flex" gap={1.5} px={2} pb={1} flexWrap="wrap" alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                gap={1.5}
                px={2}
                py={1}
              >
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  maxDate={endDate || new Date()}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
                  
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  minDate={startDate}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: {
                        minWidth: 160,
                        "& .MuiInputBase-root": { height: 36, fontSize: 13 },
                        "& .MuiInputLabel-root": { fontSize: 13 },
                      },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  fullWidth={isMobile}
                  sx={{
                    height: 36,
                    minWidth: 120,
                    fontSize: 13,
                    textTransform: "none",
                    px: 2,
                    whiteSpace: "nowrap",
                  }}
                    onClick={handleGetDetails}
                >
                  Get Details
                </Button>
              </Box>
            </LocalizationProvider>
      </Box>

        <CardContent sx={{ height: "580px", width: "100%" }}>
          <TableCompo
            columns={columns}
            data={[...JSON.parse(JSON.stringify(diamondPointOrdersData))]}
            totalCount={totalDiamondPoint}
            page={page}
            limit={limit}
            onTableChange={handleTableChange}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadStoreCsv}
              >
                Download CSV
              </Button>
            }
          />
        </CardContent>
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Redeem Order Details
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
              padding: 0,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <p>Please Enter OTP</p>
          <TextField
            fullWidth
            margin="normal"
            label="Enter OTP"
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d{0,4}$/.test(value)) {
                setInputValue(value);
              }
            }}
            inputProps={{ maxLength: 4 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprove}
            sx={{ mt: 2 }}
          >
            Approve
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DiamondPointsOrder;
